import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Eye, Filter, Search, 
  Sparkles, Users, User, Target,
  MessageSquare, Video, Trophy, BookOpen, ClipboardList,
  CheckCircle, Clock, XCircle, BarChart3, Flame, Bell, MousePointer, Mail
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import CreateInterventionWizard from '@/components/admin/CreateInterventionWizard';
import { SiteFilter } from '@/components/admin/SiteFilter';
import SurveyResults from '@/components/admin/SurveyResults';
import RecordingsView from '@/components/admin/RecordingsView';
import LearningAnalytics from '@/components/admin/LearningAnalytics';
import ObservationsView from '@/components/admin/ObservationsView';
import ChallengesView from '@/components/admin/ChallengesView';
import { PopupAnalyticsView } from '@/components/admin/PopupAnalyticsView';
import { CommitmentView } from '@/components/admin/CommitmentView';
import DailyMessageConfig from '@/components/admin/DailyMessageConfig';

interface AdminInterventionsProps {
  siteId?: string | null;
}

interface Intervention {
  id: string;
  delivery_method: string;
  category: string;
  target_type: string;
  target_worker_id: string | null;
  target_cluster_id: string | null;
  target_team: string | null;
  trigger_type: string;
  title: string;
  message: string | null;
  xp_reward: number;
  status: string;
  created_at: string;
  delivered_at: string | null;
  completed_at: string | null;
  outcome_data: Record<string, any>;
  target_worker?: { display_name: string } | null;
  target_cluster?: { cluster_name: string } | null;
  // For learning modules displayed as interventions
  source_type?: 'intervention' | 'learning_module';
}

interface InterventionStats {
  total: number;
  pending: number;
  delivered: number;
  completed: number;
  dismissed: number;
  completionRate: number;
}

const CATEGORY_INFO: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  micro_learning: { icon: <BookOpen className="h-4 w-4" />, label: 'Micro Learning', color: 'bg-green-500/10 text-green-500' },
  survey: { icon: <ClipboardList className="h-4 w-4" />, label: 'Survey', color: 'bg-blue-500/10 text-blue-500' },
  challenge: { icon: <Target className="h-4 w-4" />, label: 'Challenge', color: 'bg-purple-500/10 text-purple-500' },
  commitment: { icon: <CheckCircle className="h-4 w-4" />, label: 'Commitment', color: 'bg-amber-500/10 text-amber-500' },
  norm_nudge: { icon: <BarChart3 className="h-4 w-4" />, label: 'Norm Nudge', color: 'bg-cyan-500/10 text-cyan-500' },
  recognition: { icon: <Trophy className="h-4 w-4" />, label: 'Recognition', color: 'bg-pink-500/10 text-pink-500' },
  success_story: { icon: <Sparkles className="h-4 w-4" />, label: 'Success Story', color: 'bg-orange-500/10 text-orange-500' },
  video_message: { icon: <Video className="h-4 w-4" />, label: 'Video Message', color: 'bg-red-500/10 text-red-500' },
  observation: { icon: <Eye className="h-4 w-4" />, label: 'Observation', color: 'bg-indigo-500/10 text-indigo-500' },
};

const STATUS_INFO: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-yellow-500/10 text-yellow-500' },
  delivered: { label: 'Delivered', color: 'bg-blue-500/10 text-blue-500' },
  viewed: { label: 'Viewed', color: 'bg-cyan-500/10 text-cyan-500' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500' },
  dismissed: { label: 'Dismissed', color: 'bg-gray-500/10 text-gray-500' },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-500' },
};

export default function AdminInterventions({ siteId: propSiteId }: AdminInterventionsProps = {}) {
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [stats, setStats] = useState<InterventionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDelivery, setFilterDelivery] = useState<string>('all');
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Use prop siteId if provided
  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (profile?.company_id) {
        setCompanyId(profile.company_id);
        await loadInterventions(profile.company_id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load interventions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadInterventions(companyId);
    }
  }, [companyId, selectedSiteFilter]);

  const loadInterventions = async (companyId: string) => {
    // Load interventions with site filtering
    let query = supabase
      .from('interventions')
      .select(`
        *,
        target_worker:worker_profiles!target_worker_id(display_name, site_id),
        target_cluster:worker_clusters!target_cluster_id(cluster_name)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    // Filter by site_id if selected
    if (selectedSiteFilter !== 'all') {
      query = query.or(`site_id.eq.${selectedSiteFilter},target_type.eq.all`);
    }

    const { data, error } = await query;

    if (error) throw error;

    let interventionData = (data || []).map(i => ({
      ...i,
      source_type: 'intervention' as const
    })) as unknown as (Intervention & { target_worker?: { display_name: string; site_id?: string } })[];

    // Also load learning modules as part of the intervention history
    let learningQuery = supabase
      .from('learning_modules')
      .select('id, title, description, category, xp_reward, created_at, is_active')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(50);

    const { data: learningModules } = await learningQuery;

    // Convert learning modules to intervention format for unified display
    const learningAsInterventions: Intervention[] = (learningModules || []).map(lm => ({
      id: lm.id,
      delivery_method: 'quest',
      category: 'micro_learning',
      target_type: 'all',
      target_worker_id: null,
      target_cluster_id: null,
      target_team: null,
      trigger_type: 'manual',
      title: lm.title,
      message: lm.description,
      xp_reward: lm.xp_reward || 0,
      status: lm.is_active ? 'delivered' : 'completed',
      created_at: lm.created_at,
      delivered_at: lm.created_at,
      completed_at: null,
      outcome_data: {},
      source_type: 'learning_module' as const
    }));

    // Combine and sort by date
    const combined = [...interventionData, ...learningAsInterventions].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    
    setInterventions(combined);

    // Calculate stats
    const total = combined.length;
    const pending = combined.filter(i => i.status === 'pending').length;
    const delivered = combined.filter(i => i.status === 'delivered').length;
    const completed = combined.filter(i => i.status === 'completed').length;
    const dismissed = combined.filter(i => i.status === 'dismissed').length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    setStats({ total, pending, delivered, completed, dismissed, completionRate });
  };

  const filteredInterventions = interventions.filter(i => {
    const matchesSearch = i.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || i.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || i.status === filterStatus;
    const matchesDelivery = filterDelivery === 'all' || i.delivery_method === filterDelivery;
    return matchesSearch && matchesCategory && matchesStatus && matchesDelivery;
  });

  const getTargetDisplay = (intervention: Intervention) => {
    if (intervention.target_type === 'individual' && intervention.target_worker) {
      return intervention.target_worker.display_name;
    }
    if (intervention.target_type === 'cluster' && intervention.target_cluster) {
      return intervention.target_cluster.cluster_name;
    }
    if (intervention.target_type === 'team' && intervention.target_team) {
      return intervention.target_team;
    }
    if (intervention.target_type === 'all') {
      return 'All Workers';
    }
    return 'Unknown';
  };

  const handleInterventionCreated = () => {
    setShowCreateWizard(false);
    if (companyId) {
      loadInterventions(companyId);
    }
    toast.success('Intervention created and delivered!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Interventions</h1>
              <p className="text-sm text-muted-foreground">Manage behavioral interventions</p>
            </div>
          </div>
          <Button onClick={() => navigate('/admin/create-intervention')}>
            <Plus className="h-4 w-4 mr-2" />
            Create
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Delivered</p>
              <p className="text-2xl font-bold text-blue-500">{stats.delivered}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-green-500">{stats.completed}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Completion Rate</p>
              <p className="text-2xl font-bold">{stats.completionRate}%</p>
            </Card>
          </div>
        )}

        {/* Category Tabs - Improved Layout */}
        <Tabs defaultValue="all" className="w-full">
          <div className="relative">
            <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
              <TabsList className="inline-flex w-auto min-w-full gap-1 p-1 h-auto bg-muted/50 rounded-lg">
                <TabsTrigger value="all" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Sparkles className="h-4 w-4" />
                  <span className="hidden sm:inline">All</span>
                </TabsTrigger>
                <TabsTrigger value="survey" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <ClipboardList className="h-4 w-4" />
                  <span className="hidden sm:inline">Survey</span>
                </TabsTrigger>
                <TabsTrigger value="recording" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Video className="h-4 w-4" />
                  <span className="hidden sm:inline">Recordings</span>
                </TabsTrigger>
                <TabsTrigger value="learning" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <BookOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Learning</span>
                </TabsTrigger>
                <TabsTrigger value="observation" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Observations</span>
                </TabsTrigger>
                <TabsTrigger value="challenge" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Flame className="h-4 w-4" />
                  <span className="hidden sm:inline">Challenges</span>
                </TabsTrigger>
                <TabsTrigger value="popup" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <MousePointer className="h-4 w-4" />
                  <span className="hidden sm:inline">Popups</span>
                </TabsTrigger>
                <TabsTrigger value="commitment" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Bell className="h-4 w-4" />
                  <span className="hidden sm:inline">Commitments</span>
                </TabsTrigger>
                <TabsTrigger value="daily_message" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md whitespace-nowrap">
                  <Mail className="h-4 w-4" />
                  <span className="hidden sm:inline">Daily Messages</span>
                </TabsTrigger>
              </TabsList>
            </div>
            {/* Fade indicators for scroll */}
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-background to-transparent pointer-events-none sm:hidden" />
            <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-background to-transparent pointer-events-none sm:hidden" />
          </div>

          {/* All Interventions Tab */}
          <TabsContent value="all" className="mt-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search interventions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {!propSiteId && (
                <SiteFilter 
                  value={selectedSiteFilter} 
                  onChange={setSelectedSiteFilter}
                  className="w-[180px]"
                />
              )}
              <Select value={filterDelivery} onValueChange={setFilterDelivery}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Delivery" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Delivery</SelectItem>
                  <SelectItem value="popup">Popup</SelectItem>
                  <SelectItem value="quest">Quest</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Interventions List */}
            <div className="space-y-3">
              {filteredInterventions.length === 0 ? (
                <Card className="p-8 text-center">
                  <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No interventions found</p>
                  <Button className="mt-4" onClick={() => setShowCreateWizard(true)}>
                    Create First Intervention
                  </Button>
                </Card>
              ) : (
                filteredInterventions.map((intervention) => {
                  const categoryInfo = CATEGORY_INFO[intervention.category] || { icon: <MessageSquare className="h-4 w-4" />, label: intervention.category, color: 'bg-gray-500/10 text-gray-500' };
                  const statusInfo = STATUS_INFO[intervention.status] || { label: intervention.status, color: 'bg-gray-500/10 text-gray-500' };

                  return (
                    <Card 
                      key={intervention.id} 
                      className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedIntervention(intervention)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryInfo.color}`}>
                          {categoryInfo.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold truncate">{intervention.title}</h3>
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <Badge variant="outline" className="text-xs">
                                  {intervention.delivery_method}
                                </Badge>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  {intervention.target_type === 'individual' ? (
                                    <User className="h-3 w-3" />
                                  ) : intervention.target_type === 'cluster' ? (
                                    <Users className="h-3 w-3" />
                                  ) : (
                                    <Target className="h-3 w-3" />
                                  )}
                                  {getTargetDisplay(intervention)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge className={`text-xs ${statusInfo.color}`}>
                                {statusInfo.label}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(intervention.created_at).toLocaleDateString('id-ID')}
                              </span>
                            </div>
                          </div>
                          {intervention.message && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {intervention.message}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className={intervention.trigger_type === 'automatic' ? 'text-cyan-500' : 'text-amber-500'}>
                              {intervention.trigger_type === 'automatic' ? '⚡ Auto' : '✋ Manual'}
                            </span>
                            {intervention.xp_reward > 0 && (
                              <span className="text-amber-500">+{intervention.xp_reward} XP</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Survey Tab */}
          <TabsContent value="survey" className="mt-4">
            {companyId && <SurveyResults companyId={companyId} />}
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recording" className="mt-4">
            {companyId && <RecordingsView companyId={companyId} />}
          </TabsContent>

          {/* Learning Tab */}
          <TabsContent value="learning" className="mt-4">
            {companyId && <LearningAnalytics companyId={companyId} />}
          </TabsContent>

          {/* Observations Tab */}
          <TabsContent value="observation" className="mt-4">
            {companyId && <ObservationsView companyId={companyId} />}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenge" className="mt-4">
            {companyId && <ChallengesView companyId={companyId} />}
          </TabsContent>

          {/* Popup Analytics Tab */}
          <TabsContent value="popup" className="mt-4">
            {companyId && <PopupAnalyticsView companyId={companyId} />}
          </TabsContent>

          {/* Commitment Tab */}
          <TabsContent value="commitment" className="mt-4">
            {companyId && <CommitmentView companyId={companyId} />}
          </TabsContent>

          {/* Daily Messages Tab */}
          <TabsContent value="daily_message" className="mt-4">
            {companyId && <DailyMessageConfig companyId={companyId} siteId={selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined} />}
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedIntervention} onOpenChange={() => setSelectedIntervention(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Intervention Detail</DialogTitle>
          </DialogHeader>
          {selectedIntervention && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedIntervention.title}</h3>
                {selectedIntervention.message && (
                  <p className="text-muted-foreground mt-1">{selectedIntervention.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Delivery</p>
                  <p className="font-medium capitalize">{selectedIntervention.delivery_method}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Category</p>
                  <p className="font-medium">{CATEGORY_INFO[selectedIntervention.category]?.label || selectedIntervention.category}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Target</p>
                  <p className="font-medium">{getTargetDisplay(selectedIntervention)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge className={STATUS_INFO[selectedIntervention.status]?.color}>
                    {STATUS_INFO[selectedIntervention.status]?.label || selectedIntervention.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Trigger</p>
                  <p className="font-medium capitalize">{selectedIntervention.trigger_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">XP Reward</p>
                  <p className="font-medium">{selectedIntervention.xp_reward}</p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{new Date(selectedIntervention.created_at).toLocaleString('id-ID')}</span>
                </div>
                {selectedIntervention.delivered_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivered</span>
                    <span>{new Date(selectedIntervention.delivered_at).toLocaleString('id-ID')}</span>
                  </div>
                )}
                {selectedIntervention.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Completed</span>
                    <span>{new Date(selectedIntervention.completed_at).toLocaleString('id-ID')}</span>
                  </div>
                )}
              </div>

              {Object.keys(selectedIntervention.outcome_data || {}).length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Outcome Data</p>
                  <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(selectedIntervention.outcome_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Wizard Dialog */}
      <Dialog open={showCreateWizard} onOpenChange={setShowCreateWizard}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Intervention</DialogTitle>
          </DialogHeader>
          {companyId && (
            <CreateInterventionWizard
              companyId={companyId}
              onComplete={handleInterventionCreated}
              onCancel={() => setShowCreateWizard(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}